// src/features/projects/screens/ProjectDetailScreen.tsx

import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './ProjectDetailScreen.module.scss';
import { Projects } from '../types/project';

import ProjectHeroSection from '../components/ProjectHeroSection';
import ProjectTechnologies from '../components/ProjectTechnologies';
import ProjectContentSections from '../components/ProjectContentSections';
import ProjectAchievements from '../components/ProjectAchievements';
import ProjectFooterCTA from '../components/ProjectFooterCTA';

const ProjectDetailScreen: React.FC = () => {
  const { i18n } = useTranslation('projects');
  const { id } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Instant scroll to top - no smooth scrolling to avoid conflicts
    window.scrollTo(0, 0);

    // Simple intersection observer for scroll animations that don't block scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all fade-in sections
    const sections = containerRef.current?.querySelectorAll(`.${styles.fadeInSection}`);
    sections?.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const projectsData = i18n.getResourceBundle(i18n.language, 'projects') as Projects;
  if (!projectsData?.projects) {
    return <div>Loading...</div>;
  }

  const project = projectsData.projects.find((p) => p.id === id);
  if (!project) {
    return <div>Project not found.</div>;
  }

  return (
    <div ref={containerRef} className={styles.projectDetailScreen}>
      {/* Hero section */}
      <div className={styles.fadeInSection}>
        <ProjectHeroSection hero={project.hero} meta={project.meta} />
      </div>

      {/* Technologies */}
      <div className={styles.fadeInSection}>
        <ProjectTechnologies technologies={project.technologies} />
      </div>

      {/* Content sections */}
      <div className={styles.fadeInSection}>
        <ProjectContentSections sections={project.contentSections} />
      </div>

      {/* Achievements */}
      <div className={styles.fadeInSection}>
        <ProjectAchievements achievements={project.achievements} />
      </div>

      {/* CTA footer */}
      {project.callToAction && (
        <div className={styles.fadeInSection}>
          <ProjectFooterCTA callToAction={project.callToAction} />
        </div>
      )}
    </div>
  );
};

export default ProjectDetailScreen;